import clsx from "clsx";
import { motion } from "framer-motion";

import { useChromeContext } from "@components/chrome/hooks";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import { AttributionButton } from "@features/sales-v3/minimized-top-bar/components/attribution-button";
import { useFeatureFlags } from "@hooks/http";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { AttributionModelSelect } from "@shared/selects";

import { DateFilters } from "../date-filters";

export const MinimizedTopBar = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { hasFeatureFlag } = useFeatureFlags();
  const { isSidebarExpanded } = useChromeContext();
  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      level,
      dateRange,
      breakdownFilters,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setPage,
  } = useLandingPageAnalyticsContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  const isAttributionWindowVisible = accountingMode === "accrual";
  const positionTop = hasFeatureFlag("enableDashboardV2") ? 76 : 56;
  const sidebarWidth = hasFeatureFlag("enableDashboardV2")
    ? isSidebarExpanded
      ? 240
      : 73
    : 56;

  return (
    <motion.div
      key="minimized-top-bar"
      initial={{ translateY: -positionTop }}
      exit={{ translateY: -positionTop }}
      animate={{ translateY: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "bg-white border-b border-gray py-1 px-2 flex items-center justify-between fixed z-30",
      )}
      style={{ top: positionTop, width: `calc(100% - ${sidebarWidth}px)` }}
    >
      <div className="flex items-center gap-1">
        <div>
          <AttributionModelSelect
            menuClassName="w-[200px]"
            attributionModel={attributionModel}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "onChange",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel: value,
                  pageContext,
                },
              });

              setPage(1);
              setAttributionModel(value);
            }}
            label=""
            buttonRenderer={(props) => (
              <AttributionButton variant="combobox" label="Model" {...props} />
            )}
          />
        </div>
        {isAttributionWindowVisible && (
          <div>
            <AttributionWindowSelect
              menuClassName="w-[100px]"
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
              timeGranularity="daily"
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Landing Page Analytics",
                  action: "onChange",
                  additionalActionInfo: "Set Attribution Window",
                  data: {
                    newAttributionWindow: value,
                    pageContext,
                  },
                });

                setPage(1);
                setAttributionWindow(value);
              }}
              label=""
              buttonRenderer={(props) => (
                <AttributionButton
                  variant="combobox"
                  label="Window"
                  {...props}
                />
              )}
            />
          </div>
        )}
        <div>
          <NbAccountingModeSelect
            label=""
            menuClassName="w-[180px]"
            nbAccountingMode={accountingMode}
            isLoading={false}
            icon={null}
            buttonRenderer={(props) => (
              <AttributionButton
                variant="combobox"
                label="Accounting"
                {...props}
              />
            )}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "onChange",
                additionalActionInfo: "Set Accounting Mode",
                data: {
                  newAccountingMode: value,
                  pageContext,
                },
              });

              setPage(1);
              setAccountingMode(value);
            }}
          />
        </div>
        <DateFilters className="h-7" />
      </div>
    </motion.div>
  );
};
